import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

import i18n from "@/services/i18n/i18n";

import Auth from "@/views/Auth/index.vue";
import Login from "@/views/Auth/Login.vue";
import NotFound from "@/views/NotFound/index.vue";
import PermissionDenied from "@/views/PermissionDenied/index.vue";

import TheMainLayout from "@/components/Layout/TheMainLayout.vue";
import DriverView from "@/views/Driver/DriverView.vue";
import VehiclesView from "@/views/Vehicles/VehicleView.vue";
import ContractedJourneys from "@/views/ContractedJourneys/index.vue";
import Operations from "@/views/Operations/OperationView.vue";
import UserFeedbacks from "@/views/UserFeedbacks/index.vue";
import Dashboard from "@/views/Dashboard/index.vue";
import ClientsView from "@/views/Clients/ClientView.vue";
import ReseourceLogView from "@/views/ResourceLog/index.vue";

Vue.use(VueRouter);

function ensureHajjSalesOrderQuery(to, from, next) {
  const q = { ...to.query };
  let changed = false;
  if (q.type !== "charter") {
    q.type = "charter";
    changed = true;
  }
  if (String(q.hajj) !== "1") {
    q.hajj = "1";
    changed = true;
  }
  if (changed) next({ ...to, query: q });
  else next();
}

const routes = [
  { path: "/", redirect: "/auth/login" },
  {
    path: "/callback",
    name: "callback",
    component: () => import("@/views/Auth/KeycloakCallback.vue"),
  },
  {
    name: "session-expired",
    meta: { title: "session-expired", requiresAuth: true },
    path: "/session-expired",
    component: () => import("@/views/SessionExpired"),
  },
  {
    name: "privacy-policy",
    meta: { title: "privacy-policy", openURL: true },
    path: "/privacy-policy",
    component: () => import("@/views/privacy-policy/index.vue"),
  },
  {
    name: "terms",
    meta: { title: "terms-and-conditions", openURL: true },
    path: "/terms",
    component: () => import("@/views/TermsAndConditions/index.vue"),
  },
  {
    name: "trip-decode",
    meta: { title: "live-trip", openURL: true },
    path: "/trip/decode/:token",
    component: () => import("@/views/LiveTrip/index.vue"),
  },
  {
    name: "trip-ratings",
    meta: { title: "trip-ratings", openURL: true },
    path: "/trip-ratings/:tripRatingId",
    component: () => import("@/views/TripRating/index.vue"),
  },
  {
    path: "/",
    component: Auth,
    children: [
      {
        name: "login",
        meta: { title: "login", requiresAuth: false },
        path: "auth/login",
        component: Login,
      },
    ],
  },
  {
    path: "/",
    component: TheMainLayout,
    children: [
      {
        name: "check-availability",
        meta: {
          title: "check-availability",
          requiresAuth: true,
          requiresPermission: "operationsCheckAvailabilityTab",
          breadcrumb: [
            {
              text: "check-availability",
              disabled: false,
              active: false,
              to: { name: "check-availability" },
            },
          ],
        },
        path: "check-availability",
        component: () => import("@/views/CheckAvailability/index.vue"),
      },
      {
        name: "dashboard",
        meta: {
          title: "dashboard",
          requiresAuth: true,
          requiresPermission: "dashboardTab",
          breadcrumb: [
            {
              text: "dashboard",
              disabled: false,
              active: false,
              to: { name: "dashboard" },
            },
          ],
        },
        path: "dashboard/:view",
        component: Dashboard,
      },
      {
        name: "morabata-listing",
        meta: {
          title: "morabata-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "morabata-listing-title",
              disabled: false,
              active: false,
              to: { name: "morabata-listing" },
            },
          ],
        },
        path: "morabata/listing",
        component: () => import("@/views/Morabata/MorabataListing.vue"),
      },
      {
        name: "morabata-add",
        meta: {
          title: "add-morabata",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "add-morabata",
              disabled: false,
              active: false,
              to: { name: "morabata-add" },
            },
          ],
        },
        path: "morabata/add",
        component: () => import("@/views/Morabata/AddMorabata.vue"),
      },
      {
        name: "morabata-view",
        meta: {
          title: "morabata-detail-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "morabata-listing-title",
              disabled: false,
              active: false,
              to: { name: "morabata-listing" },
            },
            {
              text: "morabata-detail-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "morabata/view/:id",
        component: () => import("@/views/Morabata/MorabataDetail.vue"),
      },
      {
        name: "hajj-attendance-listing",
        meta: {
          title: "hajj-attendance-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-attendance-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-attendance-listing" },
            },
          ],
        },
        path: "hajj/attendance/listing",
        component: () =>
          import("@/views/HajjAttendance/HajjAttendanceListing.vue"),
      },
      {
        name: "hajj-attendance-entry",
        meta: {
          title: "hajj-attendance-entry-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-attendance-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-attendance-listing" },
            },
            {
              text: "hajj-attendance-entry-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/attendance/entry",
        component: () =>
          import("@/views/HajjAttendance/HajjAttendanceEntry.vue"),
      },
      {
        name: "hajj-institution-listing",
        meta: {
          title: "hajj-institution-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-institution-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-institution-listing" },
            },
          ],
        },
        path: "hajj/institutions/listing",
        component: () =>
          import("@/views/HajjInstitution/HajjInstitutionListing.vue"),
      },
      {
        name: "hajj-institution-add",
        meta: {
          title: "hajj-institution-add-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-institution-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-institution-listing" },
            },
            {
              text: "hajj-institution-add-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/institutions/add",
        component: () =>
          import("@/views/HajjInstitution/HajjInstitutionAdd.vue"),
      },
      {
        name: "hajj-service-company-listing",
        meta: {
          title: "hajj-service-company-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-service-company-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-service-company-listing" },
            },
          ],
        },
        path: "hajj/service-companies/listing",
        component: () =>
          import("@/views/HajjServiceCompany/HajjServiceCompanyListing.vue"),
      },
      {
        name: "hajj-service-company-add",
        meta: {
          title: "hajj-service-company-add-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-service-company-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-service-company-listing" },
            },
            {
              text: "hajj-service-company-add-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/service-companies/add",
        component: () =>
          import("@/views/HajjServiceCompany/HajjServiceCompanyAdd.vue"),
      },
      {
        name: "hajj-dawra-listing",
        meta: {
          title: "hajj-dawra-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-dawra-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-dawra-listing" },
            },
          ],
        },
        path: "hajj/dawra/listing",
        component: () =>
          import("@/views/HajjDawraOperation/HajjDawraOperationListing.vue"),
      },
      {
        name: "hajj-dawra-view",
        meta: {
          title: "hajj-dawra-detail-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-dawra-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-dawra-listing" },
            },
            {
              text: "hajj-dawra-detail-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/dawra/view/:id",
        component: () =>
          import("@/views/HajjDawraOperation/HajjDawraOperationDetail.vue"),
      },
      {
        name: "hajj-dawra-add",
        meta: {
          title: "hajj-dawra-add-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-dawra-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-dawra-listing" },
            },
            {
              text: "hajj-dawra-add-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/dawra/add",
        component: () =>
          import("@/views/HajjDawraOperation/HajjDawraOperationAdd.vue"),
      },
      {
        name: "hajj-garage-transfer-add",
        meta: {
          title: "hajj-garage-transfer-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-garage-transfer-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/garage-transfer/add",
        component: () =>
          import("@/views/HajjGarageTransfer/HajjGarageTransferAdd.vue"),
      },
      {
        name: "hajj-sales-order-listing",
        meta: {
          title: "hajj-sales-order-listing-title",
          requiresAuth: true,
          requiresPermission: "generateReservation",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-sales-order-listing-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/sales-order/listing",
        component: () =>
          import("@/views/HajjSalesOrder/HajjSalesOrderListing.vue"),
      },
      {
        name: "hajj-sales-order-generate-from-operations",
        meta: {
          title: "hajj-generate-from-operations-title",
          requiresAuth: true,
          requiresPermission: "generateReservation",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-generate-from-operations-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/sales-order/generate-from-operations",
        component: () =>
          import("@/views/HajjSalesOrder/HajjSalesOrderGenerateFromOperations.vue"),
      },
      {
        name: "hajj-sales-order-add",
        meta: {
          title: "hajj-sales-order-add-title",
          requiresAuth: true,
          requiresPermission: "generateReservation",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "hajj-sales-order-add-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/sales-order/add/:mode",
        beforeEnter: ensureHajjSalesOrderQuery,
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "hajj-sales-order-edit",
        meta: {
          title: "edit-reservation",
          requiresAuth: true,
          requiresPermission: "updateReservation",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "edit-reservation",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/sales-order/edit/:mode/:id",
        beforeEnter: ensureHajjSalesOrderQuery,
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "hajj-sales-order-update",
        meta: {
          title: "update-reservation",
          requiresAuth: true,
          requiresPermission: "updateReservation",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "update-reservation",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/sales-order/update/:mode/:id",
        beforeEnter: ensureHajjSalesOrderQuery,
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "hajj-client-season-pricing-listing",
        meta: {
          title: "hajj-csp-listing-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-csp-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-client-season-pricing-listing" },
            },
          ],
        },
        path: "hajj/client-season-pricing/listing",
        component: () =>
          import("@/views/HajjClientSeasonPricing/HajjClientSeasonPricingListing.vue"),
      },
      {
        name: "hajj-client-season-pricing-edit",
        meta: {
          title: "hajj-csp-edit-title",
          requiresAuth: true,
          requiresPermission: "hajjModule",
          breadcrumb: [
            {
              text: "hajj-csp-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-client-season-pricing-listing" },
            },
            {
              text: "hajj-csp-edit-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "hajj/client-season-pricing/edit/:id?",
        component: () =>
          import("@/views/HajjClientSeasonPricing/HajjClientSeasonPricingEdit.vue"),
      },
      {
        name: "operations",
        meta: {
          title: "operations",
          requiresAuth: true,
          requiresPermission: "operationsReservationTab",
          breadcrumb: [
            {
              text: "reservation-operations",
              disabled: false,
              active: false,
              to: { name: "operations" },
              customGoBack: true,
            },
          ],
        },
        path: "operations/:view",
        component: Operations,
      },
      {
        name: "blanket-orders",
        meta: {
          title: "blanket-orders-operations",
          requiresAuth: true,
          requiresPermission: "listBlanketOrder",
          breadcrumb: [
            {
              text: "blanket-orders-operations",
              disabled: false,
              active: false,
              to: { name: "blanket-orders-operations" },
            },
          ],
        },
        path: "blanket-orders",
        component: () =>
          import("@/views/BlanketOrdersOperations/BlanketOrdersListing.vue"),
      },
      {
        name: "view-taseed-operations-order",
        meta: {
          title: "taseed-operations-order-details",
          requiresAuth: true,
          requiresPermission: "listTaseedOperationOrder",
          breadcrumb: [
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "taseed-operations-order-details",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "taseed-operations-order/view/:id",
        component: () => import("@/views/TaseedOperationsOrder/Detail.vue"),
      },
      {
        name: "client-feedbacks",
        meta: {
          title: "client-feedbacks",
          requiresAuth: true,
          requiresPermission: "viewClientRatings",
          breadcrumb: [
            {
              text: "client-feedbacks",
              disabled: false,
              active: false,
              to: { name: "client-feedbacks" },
              customGoBack: true,
            },
          ],
        },
        path: "client-feedbacks",
        component: UserFeedbacks,
      },
      {
        name: "add-blanket-order",
        meta: {
          title: "create-blanket-order",
          requiresAuth: true,
          requiresPermission: "createBlanketOrder",
          breadcrumb: [
            {
              text: "blanket-orders-operations",
              disabled: false,
              active: false,
              to: "/blanket-orders",
            },
            {
              text: "create-blanket-order",
              disabled: false,
              active: true,
              to: "/blanket-order/add/:mode",
            },
          ],
        },
        path: "blanket-order/add/:mode",
        component: () =>
          import("@/views/BlanketOrdersOperations/CreateBlanketOrder/index.vue"),
      },
      {
        name: "add-taseed-operations-order",
        meta: {
          title: "create-taseed-operations-order",
          requiresAuth: true,
          requiresPermission: "createTaseedOperationOrder",
          breadcrumb: [
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "create-taseed-operations-order",
              disabled: false,
              active: true,
            },
          ],
        },
        path: "taseed-operations-order/add/:mode",
        component: () => import("@/views/TaseedOperationsOrder/Form.vue"),
      },
      {
        name: "edit-taseed-operations-order",
        meta: {
          title: "edit-taseed-operations-order",
          requiresAuth: true,
          requiresPermission: [
            "updateTaseedOperationOrder",
            "createTaseedOperationOrder",
          ],
          breadcrumb: [
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "edit-taseed-operations-order",
              disabled: false,
              active: true,
            },
          ],
        },
        path: "taseed-operations-order/edit/:mode/:id",
        component: () => import("@/views/TaseedOperationsOrder/Form.vue"),
      },
      {
        name: "taseed-resource-lookup",
        meta: {
          title: "taseed-resource-lookup-title",
          requiresAuth: true,
          requiresPermission: "listTaseedOperationOrder",
          breadcrumb: [
            {
              text: "taseed-resource-lookup-title",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "taseed-operations-order/resource-lookup",
        component: () => import("@/views/TaseedOperationsOrder/ResourceLookup.vue"),
      },
      {
        name: "mobile",
        meta: {
          title: "mobile-inventory",
          requiresAuth: true,
          requiresPermission: "addMobileDevice",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "mobile-inventory",
              disabled: false,
              active: false,
              to: { name: "ListMobile" },
            },
            {
              text: "add-mobile",
              disabled: true,
              active: true,
              to: "/add-mobile",
            },
          ],
        },
        path: "add-mobile",
        component: () => import("@/views/MobileInventory/AddView.vue"),
      },
      {
        name: "shift",
        meta: {
          title: "shift",
          requiresAuth: true,
          requiresPermission: "addShift",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "shift-listing",
              disabled: false,
              active: false,
              to: "/shift-listing",
            },
            {
              text: "add-shift",
              disabled: true,
              active: true,
              to: "/add-shift",
            },
          ],
        },
        path: "add-shift",
        component: () => import("@/views/SystemManagement/Shifts/Form"),
      },
      {
        name: "ListShift",
        meta: {
          title: "shift-listing",
          requiresAuth: true,
          requiresPermission: "viewShift",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "shift-listing",
              disabled: false,
              active: false,
              to: "shift-listing",
            },
          ],
        },
        path: "shift-listing",
        component: () => import("@/views/SystemManagement/Shifts/Listing"),
      },
      {
        name: "edit-shift",
        meta: {
          title: "edit-shift",
          requiresAuth: true,
          requiresPermission: "editShift",
          breadcrumb: [
            {
              text: "shift-listing",
              disabled: false,
              active: false,
              to: "/shift-listing",
            },
            {
              text: "edit-shift",
              disabled: false,
              active: false,
              to: "/shift/edit/:id",
            },
          ],
        },
        path: "shift/edit/:id",
        component: () => import("@/views/SystemManagement/Shifts/Edit"),
      },
      {
        name: "ListMobile",
        meta: {
          title: "list-mobile",
          requiresAuth: true,
          requiresPermission: "viewMobileDevice",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "mobile-inventory",
              disabled: false,
              active: false,
              to: { name: "ListMobile" },
            },
          ],
        },
        path: "list-mobile",
        component: () => import("@/views/MobileInventory/ListView.vue"),
      },
      {
        name: "EditMobile",
        meta: {
          title: "list-mobile",
          requiresAuth: true,
          requiresPermission: "editMobileDevice",
          breadcrumb: [
            {
              text: "mobile-inventory",
              disabled: false,
              active: false,
              to: { name: "ListMobile" },
            },
            {
              text: "edit-mobile",
              disabled: true,
              active: true,
              to: "/edit-mobile/:id",
            },
          ],
        },
        path: "edit-mobile/:id",
        component: () => import("@/views/MobileInventory/EditView.vue"),
      },
      {
        name: "vehicle-operations",
        meta: {
          title: "vehicle-operations",
          requiresAuth: true,
          requiresPermission: "operationsVehicleTab",
          breadcrumb: [
            {
              text: "vehicle-operations",
              disabled: false,
              active: false,
              to: { name: "vehicle-operations" },
            },
          ],
        },
        path: "vehicle-operations",
        component: () => import("@/views/Operations/VehicleOperation.vue"),
      },
      {
        name: "vehicle-operations-details",
        meta: {
          title: "vehicle-operations-details",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "vehicle-operations",
              disabled: false,
              active: false,
              to: { name: "vehicle-operations" },
            },
            {
              text: "vehicle-operations-details",
              disabled: true,
              active: true,
              paramToRemove: "vehicle-operation/:id",
            },
          ],
        },
        path: "vehicle-operation/:id",
        component: () =>
          import("@/views/Operations/VehicleOperationDetails.vue"),
      },
      {
        name: "operation-details",
        meta: {
          title: "operation-details",
          requiresAuth: true,
          requiresPermission: "viewReservationOperations",
          breadcrumb: [
            {
              text: "reservation-operations",
              disabled: false,
              active: false,
              to: "/operations/list",
            },
            {
              text: "reservation-details",
              disabled: true,
              active: true,
              paramToRemove: "id",
            },
          ],
        },
        path: "reservation/:id",
        component: () => import("@/views/Operations/OperationDetails.vue"),
      },
      {
        name: "hajj-reservation-details",
        meta: {
          title: "operation-details",
          requiresAuth: true,
          requiresPermission: "viewReservationOperations",
          breadcrumb: [
            {
              text: "hajj-module",
              disabled: false,
              active: false,
              to: { name: "dashboard", params: { view: "hajj-module" } },
            },
            {
              text: "hajj-sales-order-listing-title",
              disabled: false,
              active: false,
              to: { name: "hajj-sales-order-listing" },
            },
            {
              text: "reservation-details",
              disabled: true,
              active: true,
              paramToRemove: "id",
            },
          ],
        },
        path: "hajj-reservation/:id",
        component: () => import("@/views/Operations/OperationDetails.vue"),
      },
      {
        name: "add-contract",
        meta: {
          title: "add-contract",
          requiresAuth: true,
          requiresPermission: "addContract",
          breadcrumb: [
            {
              text: "contract-listing",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "add-contract",
              disabled: false,
              active: false,
              to: "/contracts/add",
            },
          ],
        },
        path: "contracts/add",
        component: () => import("@/views/Contracts/Add/index.vue"),
      },
      {
        name: "edit-contract",
        meta: {
          title: "edit-contract",
          requiresAuth: true,
          requiresPermission: "editContract",
          breadcrumb: [
            {
              text: "contract-listing",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "edit-contract",
              disabled: false,
              active: false,
              to: "/contracts/edit/:id",
            },
          ],
        },
        path: "contracts/edit/:id",
        component: () => import("@/views/Contracts/Edit/index.vue"),
      },
      {
        name: "update-contract",
        meta: {
          title: "update-contract",
          requiresAuth: true,
          requiresPermission: "updateContract",
          breadcrumb: [
            {
              text: "contract-listing",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "update-contract",
              disabled: false,
              active: false,
              to: "/contracts/update/:id",
            },
          ],
        },
        path: "contracts/update/:id",
        component: () => import("@/views/Contracts/Update/index.vue"),
      },
      {
        name: "contract-details",
        meta: {
          title: "contract-details",
          requiresAuth: true,
          requiresPermission: "viewContract",
          breadcrumb: [
            {
              text: "contracts",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "Contract#",
              disabled: false,
              active: false,
              to: "/contract/:id",
            },
          ],
        },
        path: "contract/:id",
        component: () => import("@/views/Contracts/Details/index.vue"),
      },
      {
        name: "route-details",
        meta: {
          title: "route-details",
          requiresAuth: true,
          requiresPermission: "viewReservationOperations", // TODO: Add permission
          breadcrumb: [
            {
              text: "contracts",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "Contract#",
              disabled: false,
              active: false,
              to: "/contract/:id",
            },
            {
              text: "Routes#",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "contract/:id/route/:routeId",
        component: () =>
          import("@/views/Contracts/Details/Routes/Details/index.vue"),
      },
      {
        name: "vehicles",
        meta: {
          title: "vehicles",
          requiresAuth: true,
          requiresPermission: "vehicleTab",
          breadcrumb: [
            {
              text: "vehicles",
              disabled: false,
              active: false,
              to: { name: "vehicles" },
            },
          ],
        },
        path: "vehicles",
        component: VehiclesView,
      },
      {
        name: "SystemManagementView",
        meta: {
          title: "system-management-view",
          requiresAuth: true,
          requiresPermission: "systemManagementTab",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
          ],
        },
        path: "system-management",
        component: () =>
          import("@/views/SystemManagement/SystemManagementView.vue"),
      },
      {
        name: "CompanyProfile",
        meta: {
          title: "company-profile",
          requiresAuth: true,
          requiresPermission: "companyProfileTab",
          breadcrumb: [
            {
              text: "company-profile",
              disabled: false,
              active: false,
              to: "/company-profile",
            },
          ],
        },
        path: "company-profile",
        component: () =>
          import("@/views/CompanyProfile/CompanyProfileView.vue"),
      },
      {
        name: "EditCompanyProfile",
        meta: {
          title: "edit-company-profile",
          requiresAuth: true,
          requiresPermission: "updateCompanyProfile",
          breadcrumb: [
            {
              text: "company-profile",
              disabled: false,
              active: false,
              to: "/company-profile",
            },
            {
              text: "edit-company-profile",
              disabled: true,
              active: true,
              paramToRemove: "id",
            },
          ],
        },
        path: "company/edit/:id",
        component: () =>
          import("@/views/CompanyProfile/EditCompanyProfile.vue"),
      },
      {
        name: "addCategory",
        meta: {
          title: "add-category",
          requiresAuth: true,
          requiresPermission: "addCategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "vehicle-categories",
              disabled: false,
              active: false,
              to: "/category-listing",
            },
            {
              text: "add-category",
              disabled: true,
              active: true,
              paramToRemove: "addCategory",
            },
          ],
        },
        path: "addCategory",
        component: () =>
          import("@/views/SystemManagement/Category/addCategory.vue"),
      },
      {
        name: "EditCategory",
        meta: {
          title: "edit-category",
          requiresAuth: true,
          requiresPermission: "editCategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "vehicle-categories",
              disabled: false,
              active: false,
              to: "/category-listing",
            },
            {
              text: "edit-category",
              disabled: true,
              active: true,
              paramToRemove: "category/edit/:id",
            },
          ],
        },
        path: "category/edit/:id",
        component: () =>
          import("@/views/SystemManagement/Category/EditCategory.vue"),
      },
      {
        name: "EditSubCategory",
        meta: {
          title: "edit-sub-category",
          requiresAuth: true,
          requiresPermission: "editSubcategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "sub-categories",
              disabled: false,
              active: false,
              to: "/sub-category-listing",
            },
            {
              text: "edit-sub-category",
              disabled: true,
              active: true,
              paramToRemove: "sub-category/edit/:id",
            },
          ],
        },
        path: "sub-category/edit/:id",
        component: () =>
          import("@/views/SystemManagement/SubCategory/EditSubCategory.vue"),
      },
      {
        name: "addSubCategory",
        meta: {
          title: "add-sub-category",
          requiresAuth: true,
          requiresPermission: "addSubcategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "sub-categories",
              disabled: false,
              active: false,
              to: "/sub-category-listing",
            },
            {
              text: "add-sub-category",
              disabled: true,
              active: true,
              paramToRemove: "addSubCategory",
            },
          ],
        },
        path: "addSubCategory",
        component: () =>
          import("@/views/SystemManagement/SubCategory/addSubCategory.vue"),
      },
      {
        name: "CategoryListing",
        meta: {
          title: "category-listing",
          requiresAuth: true,
          requiresPermission: "viewCategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "vehicle-categories",
              disabled: false,
              active: false,
              to: "/category-listing",
            },
          ],
        },
        path: "category-listing",
        component: () =>
          import("@/views/SystemManagement/Category/CategoryListing"),
      },
      {
        name: "addAdmin",
        meta: {
          title: "add-admin",
          requiresAuth: true,
          requiresPermission: "addUser",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "admins",
              disabled: false,
              active: false,
              to: "/admin-listing",
            },
            {
              text: "add-admin",
              disabled: false,
              active: false,
              to: "/admin-add",
            },
          ],
        },
        path: "/admin-add",
        component: () => import("@/views/SystemManagement/Admin/add"),
      },
      {
        name: "EditAdmin",
        meta: {
          title: "edit-admin",
          requiresAuth: true,
          requiresPermission: "editUser",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "admins",
              disabled: false,
              active: false,
              to: "/admin-listing",
            },
            {
              text: "edit-admin",
              disabled: false,
              active: false,
              to: "/admin/edit/:id",
            },
          ],
        },
        path: "admin/edit/:id",
        component: () => import("@/views/SystemManagement/Admin/edit"),
      },
      {
        name: "AdminListing",
        meta: {
          title: "admin-listing",
          requiresAuth: true,
          requiresPermission: "viewUser",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "admins",
              disabled: false,
              active: false,
              to: "/admin-listing",
            },
          ],
        },
        path: "admin-listing",
        component: () => import("@/views/SystemManagement/Admin/list"),
      },
      {
        name: "addRole",
        meta: {
          title: "add-role",
          requiresAuth: true,
          requiresPermission: "addRole",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "roles",
              disabled: false,
              active: false,
              to: "/role-listing",
            },
            {
              text: "add-role",
              disabled: false,
              active: false,
              to: "/role-add",
            },
          ],
        },
        path: "/role-add",
        component: () => import("@/views/SystemManagement/Role/addRole"),
      },
      {
        name: "EditRole",
        meta: {
          title: "edit-role",
          requiresAuth: true,
          requiresPermission: "editRole",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "roles",
              disabled: false,
              active: false,
              to: "/role-listing",
            },
            {
              text: "edit-role",
              disabled: false,
              active: false,
              to: "/role/edit/:id",
            },
          ],
        },
        path: "role/edit/:id",
        component: () => import("@/views/SystemManagement/Role/EditRole"),
      },
      {
        name: "RoleListing",
        meta: {
          title: "role-listing",
          requiresAuth: true,
          requiresPermission: "viewRole",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "roles",
              disabled: false,
              active: false,
              to: "/role-listing",
            },
          ],
        },
        path: "role-listing",
        component: () => import("@/views/SystemManagement/Role/RoleListing"),
      },
      {
        name: "SubCategoryListing",
        meta: {
          title: "sub-category-listing",
          requiresAuth: true,
          requiresPermission: "viewSubcategory",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "sub-categories",
              disabled: false,
              active: false,
              to: "/sub-category-listing",
            },
          ],
        },
        path: "sub-category-listing",
        component: () =>
          import("@/views/SystemManagement/SubCategory/SubCategoryListing.vue"),
      },
      {
        name: "AddTrainingType",
        meta: {
          title: "add-training-type",
          requiresAuth: true,
          requiresPermission: "addTrainingType",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "training-types",
              disabled: false,
              active: false,
              to: "/training-type-listing",
            },
            {
              text: "add-training-type",
              disabled: false,
              active: false,
              to: "/training-type-add",
            },
          ],
        },
        path: "/training-type-add",
        component: () =>
          import("@/views/SystemManagement/TrainingType/Form/index"),
      },
      {
        name: "EditTrainingType",
        meta: {
          title: "edit-training-type",
          requiresAuth: true,
          requiresPermission: "editTrainingType",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "training-types",
              disabled: false,
              active: false,
              to: "/training-type-listing",
            },
            {
              text: "edit-training-type",
              disabled: false,
              active: false,
              to: "/training-type/edit/:id",
            },
          ],
        },
        path: "training-type/edit/:id",
        component: () =>
          import("@/views/SystemManagement/TrainingType/Form/index"),
      },
      {
        name: "TrainingTypeListing",
        meta: {
          title: "training-type-listing",
          requiresAuth: true,
          requiresPermission: "viewTrainingType",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "training-types",
              disabled: false,
              active: false,
              to: "/training-type-listing",
            },
          ],
        },
        path: "training-type-listing",
        component: () => import("@/views/SystemManagement/TrainingType/list"),
      },
      {
        name: "AddTGAType",
        meta: {
          title: "add-tga-type",
          requiresAuth: true,
          requiresPermission: "addTga",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "tga-types",
              disabled: false,
              active: false,
              to: "/tga-type-listing",
            },
            {
              text: "add-tga-type",
              disabled: false,
              active: false,
              to: "/tga-type-add",
            },
          ],
        },
        path: "/tga-type-add",
        component: () => import("@/views/SystemManagement/TGAType/Form/index"),
      },
      {
        name: "EditTGAType",
        meta: {
          title: "edit-tga-type",
          requiresAuth: true,
          requiresPermission: "editTga",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "tga-types",
              disabled: false,
              active: false,
              to: "/tga-type-listing",
            },
            {
              text: "edit-tga-type",
              disabled: false,
              active: false,
              to: "/tga-type/edit/:id",
            },
          ],
        },
        path: "tga-type/edit/:id",
        component: () => import("@/views/SystemManagement/TGAType/Form/index"),
      },
      {
        name: "TGATypeListing",
        meta: {
          title: "tga-type-listing",
          requiresAuth: true,
          requiresPermission: "viewTga",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "tga-types",
              disabled: false,
              active: false,
              to: "/tga-type-listing",
            },
          ],
        },
        path: "tga-type-listing",
        component: () => import("@/views/SystemManagement/TGAType/list"),
      },
      {
        name: "add-vehicle",
        meta: {
          title: "add-vehicle",
          requiresAuth: true,
          requiresPermission: "addVehicle",
          breadcrumb: [
            {
              text: "vehicles",
              disabled: false,
              active: false,
              to: { name: "vehicles" },
            },
            {
              text: "add-vehicle",
              disabled: false,
              active: false,
              to: "/vehicle/add",
            },
          ],
        },
        path: "vehicle/add",
        component: () => import("@/views/Vehicles/AddVehicles.vue"),
      },
      {
        name: "edit-vehicle",
        meta: {
          title: "edit-vehicle",
          requiresAuth: true,
          requiresPermission: "editVehicle",
          breadcrumb: [
            {
              text: "vehicles",
              disabled: false,
              active: false,
              to: { name: "vehicles" },
            },
            {
              text: "edit-vehicle",
              disabled: false,
              active: false,
              to: "/vehicle/edit/:id",
            },
          ],
        },
        path: "vehicle/edit/:id",
        component: () => import("@/views/Vehicles/EditVehicles.vue"),
      },
      {
        name: "vehicle-details",
        meta: {
          title: "vehicle-details",
          requiresAuth: true,
          requiresPermission: "viewVehicle",
          breadcrumb: [
            {
              text: "vehicles",
              disabled: false,
              active: false,
              to: "/vehicles",
            },
            {
              text: "vehicle-details",
              disabled: true,
              active: true,
              paramToRemove: "vehicle/id",
            },
          ],
        },
        path: "vehicle/:id",
        component: () => import("@/views/Vehicles/VehiclesDetails.vue"),
      },
      {
        name: "drivers",
        meta: {
          title: "driver",
          requiresAuth: true,
          requiresPermission: "driverTab",
          breadcrumb: [
            {
              text: "drivers",
              disabled: false,
              active: false,
              to: { name: "drivers" },
            },
          ],
        },
        path: "drivers",
        component: DriverView,
      },
      {
        name: "add-driver",
        meta: {
          title: "add-driver",
          requiresPermission: "addDriver",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "drivers",
              disabled: false,
              active: false,
              to: { name: "drivers" },
            },
            {
              text: "add-driver",
              disabled: false,
              active: false,
              to: "/driver/add",
            },
          ],
        },
        path: "driver/add",
        component: () => import("@/views/Driver/AddDriver.vue"),
      },
      {
        name: "edit-driver",
        meta: {
          title: "edit-driver",
          requiresAuth: true,
          requiresPermission: "editDriver",
          breadcrumb: [
            {
              text: "drivers",
              disabled: false,
              active: false,
              to: { name: "drivers" },
            },
            {
              text: "edit-driver",
              disabled: false,
              active: false,
              to: "/driver/Edit/:id",
            },
          ],
        },
        path: "driver/Edit/:id",
        component: () => import("@/views/Driver/EditDriver.vue"),
      },
      {
        name: "driver-profile",
        meta: {
          title: "driver-profile",
          requiresAuth: true,
          requiresPermission: "viewDriver",
          breadcrumb: [
            {
              text: "drivers",
              disabled: false,
              active: false,
              to: { name: "drivers" },
            },
            {
              text: "driver-profile",
              disabled: false,
              active: false,
              to: "/driver/:id",
            },
          ],
        },
        path: "driver/:id",
        component: () => import("@/views/Driver/DriverProfile.vue"),
      },
      {
        name: "clients",
        meta: {
          title: "clients",
          requiresAuth: true,
          requiresPermission: "clientTab",
          breadcrumb: [
            {
              text: "clients",
              disabled: false,
              active: false,
              to: { name: "clients" },
            },
          ],
        },
        path: "clients",
        component: ClientsView,
      },
      {
        name: "main-client-accounts",
        meta: {
          title: "main-client-accounts",
          requiresAuth: true,
          requiresPermission: "clientTab",
          breadcrumb: [
            {
              text: "clients",
              disabled: false,
              active: false,
              to: { name: "clients" },
            },
            {
              text: "main-client-accounts",
              disabled: false,
              active: true,
              to: "/main-client-accounts",
            },
          ],
        },
        path: "main-client-accounts",
        component: () => import("@/views/MainClientAccounts/ClientView.vue"),
      },
      {
        name: "add-main-client-account",
        meta: {
          title: "add-main-client-account",
          requiresAuth: true,
          requiresPermission: "clientTab",
          breadcrumb: [
            {
              text: "main-client-accounts",
              disabled: false,
              active: false,
              to: "/main-client-accounts",
            },
            {
              text: "add-main-client-account",
              disabled: false,
              active: true,
              to: "/main-client-account/add",
            },
          ],
        },
        path: "main-client-account/add",
        component: () => import("@/views/MainClientAccounts/AddClient.vue"),
      },
      {
        name: "main-client-account-details",
        meta: {
          title: "main-client-account-details",
          requiresAuth: true,
          requiresPermission: "clientTab",
          breadcrumb: [
            {
              text: "main-client-accounts",
              disabled: false,
              active: false,
              to: "/main-client-accounts",
            },
            {
              text: "main-client-account-details",
              disabled: false,
              active: true,
              to: "/main-client-account/:id",
            },
          ],
        },
        path: "main-client-account/:id",
        component: () => import("@/views/MainClientAccounts/ClientDetails.vue"),
      },
      {
        name: "edit-main-client-account",
        meta: {
          title: "edit-main-client-account",
          requiresAuth: true,
          requiresPermission: "clientTab",
          breadcrumb: [
            {
              text: "main-client-accounts",
              disabled: false,
              active: false,
              to: "/main-client-accounts",
            },
            {
              text: "edit-main-client-account",
              disabled: false,
              active: true,
              to: "/main-client-account/edit/:id",
            },
          ],
        },
        path: "main-client-account/edit/:id",
        component: () => import("@/views/MainClientAccounts/EditClient.vue"),
      },
      {
        name: "add-client",
        meta: {
          title: "add-client",
          requiresAuth: true,
          requiresPermission: "addClient",
          breadcrumb: [
            {
              text: "clients",
              disabled: false,
              active: false,
              to: { name: "clients" },
            },
            {
              text: "add-client",
              disabled: false,
              active: false,
              to: "/client/add",
            },
          ],
        },
        path: "client/add",
        component: () => import("@/views/Clients/AddClient.vue"),
      },
      {
        name: "client-details",
        meta: {
          title: "client-details",
          requiresPermission: "viewClient",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "clients",
              disabled: false,
              active: false,
              to: { name: "clients" },
            },
            {
              text: "Client#",
              disabled: false,
              active: false,
              to: "/client/:id",
            },
          ],
        },
        path: "client/:id",
        component: () => import("@/views/Clients/ClientDetails.vue"),
      },
      {
        name: "client-sales-order-details",
        meta: {
          title: "reservation-details",
          requiresAuth: true,
          requiresPermission: "viewClient",
          breadcrumb: [
            {
              text: "Client#",
              disabled: false,
              active: false,
              to: "/client/:id",
            },
            {
              text: "reservation-details",
              disabled: false,
              active: true,
              to: "/client/:id/sales-order-details",
            },
          ],
        },
        path: "client/:id/sales-order-details",
        component: () => import("@/views/Clients/SalesOrderDetails.vue"),
      },
      {
        name: "client-financial-transactions",
        meta: {
          title: "financial-transactions",
          requiresAuth: true,
          requiresPermission: "viewClient",
          breadcrumb: [
            {
              text: "Client#",
              disabled: false,
              active: false,
              to: "/client/:id",
            },
            {
              text: "financial-transactions",
              disabled: false,
              active: true,
              to: "/client/:id/financial-transactions",
            },
          ],
        },
        path: "client/:id/financial-transactions",
        component: () => import("@/views/Clients/FinanceTransaction/index.vue"),
      },
      {
        name: "edit-client",
        meta: {
          title: "edit-client",
          requiresPermission: "editClient",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "clients",
              disabled: false,
              active: false,
              to: { name: "clients" },
            },
            {
              text: "Client#",
              disabled: false,
              active: false,
              to: "/client/edit/:id",
            },
          ],
        },
        path: "client/edit/:id",
        component: () => import("@/views/Clients/EditClient.vue"),
      },
      {
        name: "resource-logs",
        meta: {
          title: "resource-logs",
          requiresAuth: true,
          requiresPermission: "resourceLogView",
          breadcrumb: [
            {
              text: "resource-logs",
              disabled: false,
              active: false,
              to: { name: "resource-logs" },
            },
          ],
        },
        path: "resource-logs",
        component: ReseourceLogView,
      },
      {
        name: "add-reservation",
        meta: {
          title: "create-reservation",
          requiresAuth: true,
          requiresPermission: "generateReservation",
          breadcrumb: [
            {
              text: "reservation-operations",
              disabled: false,
              active: false,
              to: "/operations/list",
            },
            {
              text: "create-reservation",
              disabled: false,
              active: false,
              to: "/reservation/add/:mode",
            },
          ],
        },
        path: "reservation/add/:mode",
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "edit-reservation",
        meta: {
          title: "edit-reservation",
          requiresAuth: true,
          requiresPermission: "updateReservation",
          breadcrumb: [
            {
              text: "reservation-operations",
              disabled: false,
              active: false,
              to: "/operations/list",
            },
            {
              text: "edit-reservation",
              disabled: false,
              active: false,
              to: "/reservation/edit/:mode/:id",
            },
          ],
        },
        path: "reservation/edit/:mode/:id",
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "update-reservation",
        meta: {
          title: "update-reservation",
          requiresAuth: true,
          requiresPermission: "updateReservation",
          breadcrumb: [
            {
              text: "reservation-operations",
              disabled: false,
              active: false,
              to: "/operations/list",
            },
            {
              text: "update-reservation",
              disabled: false,
              active: false,
              to: "/reservation/update/:mode/:id",
            },
          ],
        },
        path: "reservation/update/:mode/:id",
        component: () => import("@/views/Reservations/CreateReservation.vue"),
      },
      {
        name: "vehicle-operations-exit-form",
        meta: {
          title: "exit-form",
          requiresAuth: true,
          requiresPermission: "createVehicleExitForm",
          breadcrumb: [
            {
              text: "vehicle-operations",
              disabled: false,
              active: false,
              to: { name: "vehicle-operations" },
            },
            {
              text: "exit-form",
              disabled: false,
              active: false,
              to: "/vehicle-operation/:id/exit-form",
            },
          ],
        },
        path: "vehicle-operation/:id/exit-form",
        component: () => import("@/views/Operations/ExitForm.vue"),
      },
      {
        name: "vehicle-operations-entry-form",
        meta: {
          title: "entry-form",
          requiresAuth: true,
          requiresPermission: "createVehicleEntryForm",
          breadcrumb: [
            {
              text: "vehicle-operations",
              disabled: false,
              active: false,
              to: { name: "vehicle-operations" },
            },
            {
              text: "entry-form",
              disabled: false,
              active: false,
              to: "/vehicle-operation/:id/entry-form",
            },
          ],
        },
        path: "vehicle-operation/:id/entry-form",
        component: () => import("@/views/Operations/EntryForm.vue"),
      },
      {
        name: "manage-trips",
        meta: {
          title: "manage-trips",
          requiresAuth: true,
          requiresPermission: "operationsContractedJourneyTab",
          breadcrumb: [
            {
              text: "manage-trips",
              disabled: false,
              active: false,
              to: "/manage-trips",
            },
          ],
        },
        path: "manage-trips",
        component: ContractedJourneys,
      },
      {
        name: "trip-import-logs",
        meta: {
          title: "trip-import-logs",
          requiresAuth: true,
          requiresPermission: "operationsContractedJourneyTab",
          breadcrumb: [
            {
              text: "manage-trips",
              disabled: false,
              active: false,
              to: "/manage-trips",
            },
            {
              text: "trip-import-logs",
              disabled: false,
              active: true,
              to: "/trip-import-logs",
            },
          ],
        },
        path: "trip-import-logs",
        component: () => import("@/views/TripImportLogs/index.vue"),
      },
      {
        name: "approval-requests",
        meta: {
          title: "approval-requests",
          requiresAuth: true,
          requiresPermission: "operationsApprovalRequestTab",
          breadcrumb: [
            {
              text: "approval-requests",
              disabled: false,
              active: false,
              to: "/approval-requests",
            },
          ],
        },
        path: "approval-requests",
        component: () => import("@/views/ApprovalRequests/index.vue"),
      },
      {
        name: "master-accounts",
        meta: {
          title: "master-accounts",
          requiresAuth: true,
          requiresPermission: "masterAccountsTab",
          breadcrumb: [
            {
              text: "master-accounts",
              disabled: false,
              active: false,
              to: "/master-accounts",
            },
          ],
        },
        path: "master-accounts",
        component: () => import("@/views/MasterAccounts/index.vue"),
      },
      {
        name: "reported-issues",
        meta: {
          title: "reported-issues",
          requiresAuth: true,
          requiresPermission: "reportedIssues",
          breadcrumb: [
            {
              text: "reported-issues",
              disabled: false,
              active: false,
              to: "/reported-issues",
            },
          ],
        },
        path: "reported-issues",
        component: () => import("@/views/ReportedIssues/index.vue"),
      },
      {
        name: "license-type-listing",
        meta: {
          title: "license-types-listing",
          requiresAuth: true,
          requiresPermission: "viewLicensetype",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "licenses-type",
              disabled: false,
              active: false,
              to: "/license-type-listing",
            },
          ],
        },
        path: "license-type-listing",
        component: () =>
          import("@/views/SystemManagement/LicenseTypes/Listing/index.vue"),
      },
      {
        name: "add-license-type",
        meta: {
          title: "add-license-type",
          requiresAuth: true,
          requiresPermission: "addLicensetype",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "licenses-type",
              disabled: false,
              active: false,
              to: "/license-type-listing",
            },
            {
              text: "add-license-type",
              disabled: true,
              active: true,
              paramToRemove: "add-license-type",
            },
          ],
        },
        path: "add-license-type",
        component: () => import("@/views/SystemManagement/LicenseTypes/Form"),
      },
      {
        name: "license-type-edit",
        meta: {
          title: "edit-license-type",
          requiresAuth: true,
          requiresPermission: "editLicensetype",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "licenses-type",
              disabled: false,
              active: false,
              to: "/license-type-listing",
            },
            {
              text: "edit-license-type",
              disabled: true,
              active: true,
              paramToRemove: "license-type-edit/:id",
            },
          ],
        },
        path: "license-type-edit/:id",
        component: () => import("@/views/SystemManagement/LicenseTypes/Form"),
      },
      {
        name: "insurance-companies-listing",
        meta: {
          title: "insurance-companies-listing",
          requiresAuth: true,
          requiresPermission: "viewInsuranceCompany",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "insurance-companies",
              disabled: false,
              active: false,
              to: "/insurance-companies-listing",
            },
          ],
        },
        path: "insurance-companies-listing",
        component: () =>
          import("@/views/SystemManagement/InsuranceCompanies/Listing"),
      },
      {
        name: "approval-time-frame",
        meta: {
          title: "approval-time-frame",
          requiresAuth: true,
          requiresPermission: "editApprovalTimeFrame",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "approval-time-frame",
              disabled: false,
              active: false,
              to: "/approval-time-frame",
            },
          ],
        },
        path: "approval-time-frame",
        component: () =>
          import("@/views/SystemManagement/ApprovalTimeFrame/Form"),
      },
      {
        name: "availability-setup",
        meta: {
          title: "availability-setup",
          requiresAuth: true,
          requiresPermission: "editAvailabilitySetUp",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "availability-setup",
              disabled: false,
              active: false,
              to: "/availability-setup",
            },
          ],
        },
        path: "availability-setup",
        component: () =>
          import("@/views/SystemManagement/AvailabilitySetup/Form"),
      },
      {
        name: "allowances-settings",
        meta: {
          title: "allowances-settings",
          requiresAuth: true,
          requiresPermission: "editAllowancesSettings",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "allowances-settings",
              disabled: false,
              active: true,
              to: "/allowances-settings",
            },
          ],
        },
        path: "allowances-settings",
        component: () =>
          import("@/views/SystemManagement/AllowancesSettings/Form"),
      },
      {
        name: "add-insurance-companies",
        meta: {
          title: "add-insurance-companies",
          requiresAuth: true,
          requiresPermission: "addInsuranceCompany",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "insurance-companies",
              disabled: false,
              active: false,
              to: "/insurance-companies-listing",
            },
            {
              text: "add-insurance-companies",
              disabled: true,
              active: true,
              paramToRemove: "add-insurance-companies",
            },
          ],
        },
        path: "add-insurance-companies",
        component: () =>
          import("@/views/SystemManagement/InsuranceCompanies/Form"),
      },
      {
        name: "insurance-companies-edit",
        meta: {
          title: "edit-insurance-companies",
          requiresAuth: true,
          requiresPermission: "editInsuranceCompany",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "insurance-companies",
              disabled: false,
              active: false,
              to: "/insurance-companies-listing",
            },
            {
              text: "edit-insurance-companies",
              disabled: true,
              active: true,
              paramToRemove: "insurance-companies-edit/:id",
            },
          ],
        },
        path: "insurance-companies-edit/:id",
        component: () =>
          import("@/views/SystemManagement/InsuranceCompanies/Form"),
      },
      {
        name: "damage-level-listing",
        meta: {
          title: "damage-level-listing",
          requiresAuth: true,
          requiresPermission: "viewDamagelevel",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "damage-level",
              disabled: false,
              active: false,
              to: "/damage-level-listing",
            },
          ],
        },
        path: "damage-level-listing",
        component: () => import("@/views/SystemManagement/DamageLevel/Listing"),
      },
      {
        name: "add-damage-level",
        meta: {
          title: "add-damage-level",
          requiresAuth: true,
          requiresPermission: "addDamagelevel",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "damage-level",
              disabled: false,
              active: false,
              to: "/damage-level-listing",
            },
            {
              text: "add-damage-level",
              disabled: true,
              active: true,
              paramToRemove: "add-damage-level",
            },
          ],
        },
        path: "add-damage-level",
        component: () => import("@/views/SystemManagement/DamageLevel/Form"),
      },
      {
        name: "damage-level-edit",
        meta: {
          title: "edit-damage-level",
          requiresAuth: true,
          requiresPermission: "editDamagelevel",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "damage-level",
              disabled: false,
              active: false,
              to: "/damage-level-listing",
            },
            {
              text: "edit-damage-level",
              disabled: true,
              active: true,
              paramToRemove: "damage-level-edit/:id",
            },
          ],
        },
        path: "damage-level-edit/:id",
        component: () => import("@/views/SystemManagement/DamageLevel/Form"),
      },
      {
        name: "exit-entry-form",
        meta: {
          title: "exit-entry-form",
          requiresAuth: true,
          requiresPermission: "addDamagelevel",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "exit-entry-form",
              disabled: false,
              active: true,
              to: "/exit-entry-form",
            },
          ],
        },
        path: "exit-entry-form",
        component: () => import("@/views/SystemManagement/ExitEntryForm/Form"),
      },
      {
        name: "pricing-lists",
        meta: {
          title: "pricing-lists",
          requiresAuth: true,
          requiresPermission: "viewPricingList",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "pricing-lists",
              disabled: false,
              active: false,
              to: "/pricing-lists",
            },
          ],
        },
        path: "pricing-lists",
        component: () =>
          import("@/views/SystemManagement/PricingListsManagement/Listing"),
      },
      {
        name: "add-pricing-list",
        meta: {
          title: "add-pricing-list",
          requiresAuth: true,
          requiresPermission: "addPricingList",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "pricing-lists",
              disabled: false,
              active: false,
              to: "/pricing-lists",
            },
            {
              text: "add-pricing-list",
              disabled: true,
              active: true,
              paramToRemove: "add-pricing-list",
            },
          ],
        },
        path: "add-pricing-list",
        component: () =>
          import("@/views/SystemManagement/PricingListsManagement/Form"),
      },
      {
        name: "view-pricing-list",
        meta: {
          title: "view-pricing-list",
          requiresPermission: "viewPricingList",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "pricing-lists",
              disabled: false,
              active: false,
              to: "/pricing-lists",
            },
            {
              text: "view-pricing-list",
              disabled: false,
              active: false,
              to: "/pricing-lists/:id",
            },
          ],
        },
        path: "pricing-lists/:id",
        component: () =>
          import("@/views/SystemManagement/PricingListsManagement/View/index.vue"),
      },
      {
        name: "product-listing",
        meta: {
          title: "product-listing",
          requiresAuth: true,
          requiresPermission: "viewProduct",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "bundles",
              disabled: false,
              active: false,
              to: "/product-listing",
            },
          ],
        },
        path: "product-listing",
        component: () =>
          import("@/views/SystemManagement/ProductsManagement/Listing"),
      },
      {
        name: "add-product",
        meta: {
          title: "add-product",
          requiresAuth: true,
          requiresPermission: "addProduct",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "bundles",
              disabled: false,
              active: false,
              to: "/product-listing",
            },
            {
              text: "add-bundle",
              disabled: true,
              active: true,
              paramToRemove: "add-product",
            },
          ],
        },
        path: "add-product",
        component: () =>
          import("@/views/SystemManagement/ProductsManagement/Form"),
      },
      {
        name: "product-edit",
        meta: {
          title: "edit-product",
          requiresAuth: true,
          requiresPermission: "editProduct",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "bundles",
              disabled: false,
              active: false,
              to: "/product-listing",
            },
            {
              text: "edit-bundle",
              disabled: true,
              active: true,
              paramToRemove: "product-edit/:id",
            },
          ],
        },
        path: "product-edit/:id",
        component: () =>
          import("@/views/SystemManagement/ProductsManagement/Form"),
      },
      {
        name: "grade-listing",
        meta: {
          title: "grade-listing",
          requiresAuth: true,
          requiresPermission: "viewClientGrade",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "grades",
              disabled: false,
              active: false,
              to: "/grade-listing",
            },
          ],
        },
        path: "grade-listing",
        component: () => import("@/views/SystemManagement/Grades/Listing"),
      },
      {
        name: "add-grade",
        meta: {
          title: "add-grade",
          requiresAuth: true,
          requiresPermission: "addClientGrade",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "grades",
              disabled: false,
              active: false,
              to: "/grade-listing",
            },
            {
              text: "add-grade",
              disabled: true,
              active: true,
              paramToRemove: "add-grade",
            },
          ],
        },
        path: "add-grade",
        component: () => import("@/views/SystemManagement/Grades/Form"),
      },
      {
        name: "grade-edit",
        meta: {
          title: "edit-grade",
          requiresAuth: true,
          requiresPermission: "editClientGrade",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "grades",
              disabled: false,
              active: false,
              to: "/grade-listing",
            },
            {
              text: "edit-grade",
              disabled: true,
              active: true,
              paramToRemove: "grade-edit/:id",
            },
          ],
        },
        path: "grade-edit/:id",
        component: () => import("@/views/SystemManagement/Grades/Form"),
      },
      {
        name: "account-listing",
        meta: {
          title: "account-listing",
          requiresAuth: true,
          requiresPermission: "financeAccountsTab",
          breadcrumb: [
            {
              text: "account",
              disabled: false,
              active: false,
              to: "/account-listing",
            },
          ],
        },
        path: "account-listing",
        component: () => import("@/views/Finances/AccountManagement/Listing"),
      },
      {
        name: "add-account",
        meta: {
          title: "add-account",
          requiresAuth: true,
          requiresPermission: "addFinanceAccount",
          breadcrumb: [
            {
              text: "account",
              disabled: false,
              active: false,
              to: "/account-listing",
            },
            {
              text: "add-account",
              disabled: true,
              active: true,
              paramToRemove: "add-account",
            },
          ],
        },
        path: "add-account",
        component: () => import("@/views/Finances/AccountManagement/Form"),
      },
      {
        name: "account-edit",
        meta: {
          title: "edit-account",
          requiresAuth: true,
          requiresPermission: "editFinanceAccount",
          breadcrumb: [
            {
              text: "account",
              disabled: false,
              active: false,
              to: "/account-listing",
            },
            {
              text: "edit-account",
              disabled: true,
              active: true,
              paramToRemove: "account-edit/:id",
            },
          ],
        },
        path: "account-edit/:id",
        component: () => import("@/views/Finances/AccountManagement/Form"),
      },
      {
        name: "proforma-invoice-listing",
        meta: {
          title: "proforma-invoice-listing",
          requiresAuth: true,
          requiresPermission: "financeProformaInvoiceTab",
          breadcrumb: [
            {
              text: "proforma-invoice",
              disabled: false,
              active: false,
              to: "/proforma-invoice-listing",
            },
          ],
        },
        path: "proforma-invoice-listing",
        component: () => import("@/views/Finances/ProformaInvoices/Listing"),
      },
      {
        name: "proforma-invoice-view",
        meta: {
          title: "view-proforma-invoice",
          requiresAuth: true,
          requiresPermission: "viewProformaInvoice",
          breadcrumb: [
            {
              text: "post-transactions",
              disabled: false,
              active: false,
              to: "/post-transactions",
            },
            {
              text: "view-proforma-invoice",
              disabled: true,
              active: true,
              paramToRemove: "proforma-invoice-view/:id",
            },
          ],
        },
        path: "proforma-invoice-view/:id",
        component: () => import("@/views/Finances/ProformaInvoices/View"),
      },
      {
        name: "add-proforma-invoice",
        meta: {
          title: "add-proforma-invoice",
          requiresAuth: true,
          requiresPermission: "addProformaInvoice",
          breadcrumb: [
            {
              text: "proforma-invoice",
              disabled: false,
              active: false,
              to: "/proforma-invoice-listing",
            },
            {
              text: "add-proforma-invoice",
              disabled: true,
              active: true,
              paramToRemove: "add-proforma-invoice",
            },
          ],
        },
        path: "add-proforma-invoice",
        component: () => import("@/views/Finances/ProformaInvoices/Add"),
      },
      {
        name: "proforma-invoice-edit",
        meta: {
          title: "edit-proforma-invoice",
          requiresAuth: true,
          requiresPermission: "editProformaInvoice",
          breadcrumb: [
            {
              text: "proforma-invoice",
              disabled: false,
              active: false,
              to: "/proforma-invoice-listing",
            },
            {
              text: "edit-proforma-invoice",
              disabled: true,
              active: true,
              paramToRemove: "proforma-invoice-edit/:id",
            },
          ],
        },
        path: "proforma-invoice-edit/:id",
        component: () => import("@/views/Finances/ProformaInvoices/Edit"),
      },
      {
        name: "jv-listing",
        meta: {
          title: "journal-voucher-listing",
          requiresAuth: true,
          requiresPermission: "financeJVTab",
          breadcrumb: [
            {
              text: "journal-voucher",
              disabled: false,
              active: false,
              to: "/jv-listing",
            },
          ],
        },
        path: "jv-listing",
        component: () => import("@/views/Finances/JVManagement/Listing"),
      },
      {
        name: "jv-view",
        meta: {
          title: "view-journal-voucher",
          requiresAuth: true,
          requiresPermission: "viewJournalVoucher",
          breadcrumb: [
            {
              text: "post-transactions",
              disabled: false,
              active: false,
              to: "/post-transactions",
            },
            {
              text: "view-journal-voucher",
              disabled: true,
              active: true,
              paramToRemove: "jv-view/:id",
            },
          ],
        },
        path: "jv-view/:id",
        component: () => import("@/views/Finances/JVManagement/View"),
      },
      {
        name: "add-jv",
        meta: {
          title: "add-journal-voucher",
          requiresAuth: true,
          requiresPermission: "addJournalVoucher",
          breadcrumb: [
            {
              text: "journal-voucher",
              disabled: false,
              active: false,
              to: "/jv-listing",
            },
            {
              text: "add-journal-voucher",
              disabled: true,
              active: true,
              paramToRemove: "add-jv",
            },
          ],
        },
        path: "add-jv",
        component: () => import("@/views/Finances/JVManagement/Form"),
      },
      {
        name: "pos-device-listing",
        meta: {
          title: "pos-device-lsting",
          requiresAuth: true,
          requiresPermission: "viewPosDevice",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "pos-device",
              disabled: false,
              active: false,
              to: "/pos-device-listing",
            },
          ],
        },
        path: "pos-device-listing",
        component: () => import("@/views/SystemManagement/POSDevice/Listing"),
      },
      {
        name: "add-pos-device",
        meta: {
          title: "add-pos-device",
          requiresAuth: true,
          requiresPermission: "addPosDevice",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "pos-device",
              disabled: false,
              active: false,
              to: "/pos-device-listing",
            },
            {
              text: "add-pos-device",
              disabled: true,
              active: true,
              paramToRemove: "add-pos-device",
            },
          ],
        },
        path: "add-pos-device",
        component: () => import("@/views/SystemManagement/POSDevice/Form"),
      },
      {
        name: "pos-device-edit",
        meta: {
          title: "edit-pos-device",
          requiresAuth: true,
          requiresPermission: "editPosDevice",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "pos-device",
              disabled: false,
              active: false,
              to: "/pos-device-listing",
            },
            {
              text: "edit-pos-device",
              disabled: true,
              active: true,
              paramToRemove: "pos-device-edit/:id",
            },
          ],
        },
        path: "pos-device-edit/:id",
        component: () => import("@/views/SystemManagement/POSDevice/Form"),
      },
      {
        name: "zone-listing",
        meta: {
          title: "zone-listing",
          requiresAuth: true,
          requiresPermission: "viewZone",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "zones",
              disabled: false,
              active: false,
              to: "/zone-listing",
            },
          ],
        },
        path: "zone-listing",
        component: () => import("@/views/SystemManagement/Zone/Listing"),
      },
      {
        name: "zone-add",
        meta: {
          title: "add-zone",
          requiresAuth: true,
          requiresPermission: "addZone",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "zones",
              disabled: false,
              active: false,
              to: "/zone-listing",
            },
            {
              text: "add-zone",
              disabled: true,
              active: true,
              paramToRemove: "zone-add",
            },
          ],
        },
        path: "zone-add",
        component: () => import("@/views/SystemManagement/Zone/Form"),
      },
      {
        name: "zone-edit",
        meta: {
          title: "edit-zone",
          requiresAuth: true,
          requiresPermission: "editZone",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "zones",
              disabled: false,
              active: false,
              to: "/zone-listing",
            },
            {
              text: "edit-zone",
              disabled: true,
              active: true,
              paramToRemove: "zone-edit/:id",
            },
          ],
        },
        path: "zone-edit/:id",
        component: () => import("@/views/SystemManagement/Zone/Form"),
      },
      {
        name: "suppliers",
        meta: {
          title: "suppliers",
          requiresAuth: true,
          requiresPermission: "supplierTab",
          breadcrumb: [
            {
              text: "suppliers",
              disabled: false,
              active: false,
              to: "/suppliers",
            },
          ],
        },
        path: "suppliers",
        component: () => import("@/views/Suppliers/SupplierView.vue"),
      },
      {
        name: "supplier-profile",
        meta: {
          title: "supplier-profile",
          requiresAuth: true,
          requiresPermission: "viewProvider",
          breadcrumb: [
            {
              text: "suppliers",
              disabled: false,
              active: false,
              to: "/suppliers",
            },
            {
              text: "supplier-details",
              disabled: false,
              active: false,
              to: "/suppliers-details/:id",
            },
          ],
        },
        path: "suppliers-details/:id",
        component: () => import("@/views/Suppliers/SupplierProfile.vue"),
      },
      {
        name: "add-supplier",
        meta: {
          title: "add-supplier",
          requiresAuth: true,
          requiresPermission: "addProvider",
          breadcrumb: [
            {
              text: "suppliers",
              disabled: false,
              active: false,
              to: "/suppliers",
            },
            {
              text: "add-supplier",
              disabled: true,
              active: true,
              paramToRemove: "/supplier/add-supplier",
            },
          ],
        },
        path: "supplier/add-supplier",
        component: () => import("@/views/Suppliers/AddSupplier.vue"),
      },
      {
        name: "edit-supplier",
        meta: {
          title: "edit-supplier",
          requiresAuth: true,
          requiresPermission: "editProvider",
          breadcrumb: [
            {
              text: "suppliers",
              disabled: false,
              active: false,
              to: "/suppliers",
            },
            {
              text: "edit-supplier",
              disabled: true,
              active: true,
              paramToRemove: "/supplier/edit/:id",
            },
          ],
        },
        path: "supplier/edit/:id",
        component: () => import("@/views/Suppliers/EditSupplier.vue"),
      },
      {
        name: "region-listing",
        meta: {
          title: "region-listing",
          requiresAuth: true,
          requiresPermission: "viewRegion",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "regions",
              disabled: false,
              active: false,
              to: "/region-listing",
            },
          ],
        },
        path: "region-listing",
        component: () => import("@/views/SystemManagement/Region/Listing"),
      },
      {
        name: "region-add",
        meta: {
          title: "add-region",
          requiresAuth: true,
          requiresPermission: "addRegion",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "regions",
              disabled: false,
              active: false,
              to: "/region-listing",
            },
            {
              text: "add-region",
              disabled: true,
              active: true,
              paramToRemove: "region-add",
            },
          ],
        },
        path: "region-add",
        component: () => import("@/views/SystemManagement/Region/From"),
      },
      {
        name: "region-edit",
        meta: {
          title: "edit-region",
          requiresAuth: true,
          requiresPermission: "editRegion",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "regions",
              disabled: false,
              active: false,
              to: "/region-listing",
            },
            {
              text: "edit-region",
              disabled: true,
              active: true,
              paramToRemove: "region-edit/:id",
            },
          ],
        },
        path: "region-edit/:id",
        component: () => import("@/views/SystemManagement/Region/From"),
      },
      {
        name: "province-listing",
        meta: {
          title: "province-listing",
          requiresAuth: true,
          requiresPermission: "viewProvince",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "provinces",
              disabled: false,
              active: false,
              to: "/province-listing",
            },
          ],
        },
        path: "province-listing",
        component: () => import("@/views/SystemManagement/Province/Listing"),
      },
      {
        name: "add-province",
        meta: {
          title: "add-province",
          requiresAuth: true,
          requiresPermission: "addProvince",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "provinces",
              disabled: false,
              active: false,
              to: "/province-listing",
            },
            {
              text: "add-province",
              disabled: true,
              active: true,
              paramToRemove: "add-province",
            },
          ],
        },
        path: "add-province",
        component: () => import("@/views/SystemManagement/Province/From"),
      },
      {
        name: "edit-province",
        meta: {
          title: "edit-province",
          requiresAuth: true,
          requiresPermission: "editProvince",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "provinces",
              disabled: false,
              active: false,
              to: "/province-listing",
            },
            {
              text: "edit-province",
              disabled: true,
              active: true,
              paramToRemove: "edit-province/:id",
            },
          ],
        },
        path: "edit-province/:id",
        component: () => import("@/views/SystemManagement/Province/From"),
      },
      {
        name: "garages-listing",
        meta: {
          title: "garages-listing",
          requiresAuth: true,
          requiresPermission: "viewGarage",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "garages",
              disabled: false,
              active: false,
              to: "/garages-listing",
            },
          ],
        },
        path: "garages-listing",
        component: () => import("@/views/SystemManagement/Garages/Listing"),
      },
      {
        name: "add-garage",
        meta: {
          title: "add-garage",
          requiresAuth: true,
          requiresPermission: "addGarage",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "garages",
              disabled: false,
              active: false,
              to: "/garages-listing",
            },
            {
              text: "add-garage",
              disabled: true,
              active: true,
              paramToRemove: "add-garage",
            },
          ],
        },
        path: "add-garage",
        component: () => import("@/views/SystemManagement/Garages/Form"),
      },
      {
        name: "edit-garage",
        meta: {
          title: "edit-garage",
          requiresAuth: true,
          requiresPermission: "editGarage",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "garages",
              disabled: false,
              active: false,
              to: "/garages-listing",
            },
            {
              text: "edit-garage",
              disabled: true,
              active: true,
              paramToRemove: "edit-garage/:id",
            },
          ],
        },
        path: "edit-garage/:id",
        component: () => import("@/views/SystemManagement/Garages/Form"),
      },
      {
        name: "finances",
        meta: {
          title: "receipt-vouchers",
          requiresPermission: "receiptVoucherTab",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "receipt-vouchers",
              disabled: false,
              active: false,
              to: { name: "finances" },
            },
          ],
        },
        path: "finances",
        component: () => import("@/views/Finances/index.vue"),
      },
      {
        name: "add-receipt-voucher",
        meta: {
          title: "create-receipt-voucher",
          requiresPermission: "addReceiptVoucher",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "receipt-vouchers",
              disabled: false,
              active: false,
              to: "/finances",
            },
            {
              text: "add-receipt-voucher",
              disabled: false,
              active: false,
              to: "/receipt-voucher/add/:mode",
            },
          ],
        },
        path: "receipt-voucher/add/:mode",
        component: () => import("@/views/Finances/Form/AddReceiptVoucher.vue"),
      },
      {
        name: "view-receipt-voucher",
        meta: {
          title: "view-receipt-voucher",
          requiresPermission: "viewReceiptVoucher",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "post-transactions",
              disabled: false,
              active: false,
              to: "/post-transactions",
            },
            {
              text: "view-receipt-voucher",
              disabled: false,
              active: false,
              to: "/receipt-voucher/view/:id",
            },
          ],
        },
        path: "receipt-voucher-view/:id",
        component: () => import("@/views/Finances/Form/ViewReceiptVoucher.vue"),
      },
      {
        name: "edit-receipt-voucher",
        meta: {
          title: "edit-receipt-voucher",
          requiresPermission: "editReceiptVoucher",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "receipt-vouchers",
              disabled: false,
              active: false,
              to: "/finances",
            },
            {
              text: "edit-receipt-voucher",
              disabled: false,
              active: false,
              to: "/receipt-voucher/edit/:mode/:id",
            },
          ],
        },
        path: "receipt-voucher/edit/:mode/:id",
        component: () => import("@/views/Finances/Form/EditReceiptVoucher.vue"),
      },
      {
        name: "branches-listing",
        meta: {
          title: "branches-listing",
          requiresAuth: true,
          requiresPermission: "viewBranch",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "branches",
              disabled: false,
              active: false,
              to: "/branches-listing",
            },
          ],
        },
        path: "branches-listing",
        component: () => import("@/views/SystemManagement/Branch/Listing"),
      },
      {
        name: "add-branch",
        meta: {
          title: "add-branch",
          requiresAuth: true,
          requiresPermission: "addBranch",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "branches",
              disabled: false,
              active: false,
              to: "/branches-listing",
            },
            {
              text: "add-branch",
              disabled: true,
              active: true,
              paramToRemove: "add-branch",
            },
          ],
        },
        path: "add-branch",
        component: () => import("@/views/SystemManagement/Branch/Form"),
      },
      {
        name: "edit-branch",
        meta: {
          title: "edit-branch",
          requiresAuth: true,
          requiresPermission: "editBranch",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "branches",
              disabled: false,
              active: false,
              to: "/branches-listing",
            },
            {
              text: "edit-branch",
              disabled: true,
              active: true,
              paramToRemove: "edit-branch/:id",
            },
          ],
        },
        path: "edit-branch/:id",
        component: () => import("@/views/SystemManagement/Branch/Form"),
      },
      {
        name: "transaction-log",
        meta: {
          title: "transaction-log",
          requiresAuth: true,
          requiresPermission: "transactionLogTab",
          breadcrumb: [
            {
              text: "transaction-log",
              disabled: false,
              active: false,
              to: "/transaction-log",
            },
          ],
        },
        path: "transaction-log",
        component: () => import("@/views/TransactionLog/Listing"),
      },
      {
        name: "driver-allowance",
        meta: {
          title: "driver-allowance",
          requiresAuth: true,
          requiresPermission: "viewDriverAllowance",
          breadcrumb: [
            {
              text: "driver-allowance",
              disabled: false,
              active: false,
              to: "/driver-allowance",
            },
          ],
        },
        path: "driver-allowance",
        component: () => import("@/views/DriverAllowance"),
      },
      {
        name: "add-driver-allowance",
        meta: {
          title: "driver-allowance",
          requiresAuth: true,
          requiresPermission: "addDriverAllowance",
          breadcrumb: [
            {
              text: "driver-allowance",
              disabled: false,
              active: false,
              to: { name: "driver-allowance" },
            },
            {
              text: "allowance-add",
              disabled: false,
              active: false,
              to: "/driver-allowance/add",
            },
          ],
        },
        path: "driver-allowance/add",
        component: () => import("@/views/DriverAllowance/AddAllowance.vue"),
      },
      {
        name: "edit-driver-allowance",
        meta: {
          title: "edit-driver-allowance",
          requiresAuth: true,
          requiresPermission: "editDriverAllowance",
          breadcrumb: [
            {
              text: "driver-allowance",
              disabled: false,
              active: false,
              to: { name: "driver-allowance" },
            },
            {
              text: "edit-driver-allowance",
              disabled: false,
              active: false,
              to: "/driver-allowance/edit/:id",
            },
          ],
        },
        path: "driver-allowance/edit/:id",
        component: () => import("@/views/DriverAllowance/EditAllowance.vue"),
      },
      {
        name: "vehicle-signal-validation",
        meta: {
          title: "vehicle-trackable",
          requiresAuth: true,
          requiresPermission: "viewDriverAllowance",
          breadcrumb: [
            {
              text: "operations",
              disabled: false,
              active: false,
              to: "/operations",
            },
            {
              text: "vehicle-trackable",
              disabled: false,
              active: false,
              to: "/vehicle-signal-validation",
            },
          ],
        },
        path: "vehicle-signal-validation",
        component: () =>
          import("@/components/Operations/VehicleSignalValidation/index.vue"),
      },
      {
        name: "services-listing",
        meta: {
          title: "service-listing",
          requiresAuth: true,
          requiresPermission: "viewService",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "services",
              disabled: false,
              active: false,
              to: "/services-listing",
            },
          ],
        },
        path: "services-listing",
        component: () => import("@/views/SystemManagement/Services/Listing"),
      },
      {
        name: "add-service",
        meta: {
          title: "add-service",
          requiresAuth: true,
          requiresPermission: "addService",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "services",
              disabled: false,
              active: false,
              to: "/services-listing",
            },
            {
              text: "add-service",
              disabled: true,
              active: true,
              paramToRemove: "add-service",
            },
          ],
        },
        path: "add-service",
        component: () => import("@/views/SystemManagement/Services/Form"),
      },
      {
        name: "edit-service",
        meta: {
          title: "edit-service",
          requiresAuth: true,
          requiresPermission: "editService",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "services",
              disabled: false,
              active: false,
              to: "/services-listing",
            },
            {
              text: "edit-service",
              disabled: true,
              active: true,
              paramToRemove: "edit-service/:id",
            },
          ],
        },
        path: "edit-service/:id",
        component: () => import("@/views/SystemManagement/Services/Form"),
      },
      {
        name: "promo-code-listing",
        meta: {
          title: "promo-codes-listing",
          requiresAuth: true,
          requiresPermission: "viewPromoCode",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "promo-codes",
              disabled: false,
              active: false,
              to: "/promo-code-listing",
            },
          ],
        },
        path: "promo-code-listing",
        component: () => import("@/views/SystemManagement/PromoCodes/Listing"),
      },
      {
        name: "promo-code-add",
        meta: {
          title: "add-promo-code",
          requiresAuth: true,
          requiresPermission: "addPromoCode",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "promo-codes",
              disabled: false,
              active: false,
              to: "/promo-code-listing",
            },
            {
              text: "add-promo-code",
              disabled: true,
              active: true,
              paramToRemove: "add-promo-code",
            },
          ],
        },
        path: "add-promo-code",
        component: () => import("@/views/SystemManagement/PromoCodes/From"),
      },
      {
        name: "edit-promo-code",
        meta: {
          title: "edit-promo-code",
          requiresAuth: true,
          requiresPermission: "editPromoCode",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "promo-codes",
              disabled: false,
              active: false,
              to: "/promo-code-listing",
            },
            {
              text: "edit-promo-code",
              disabled: true,
              active: true,
              paramToRemove: "edit-promo-code/:id",
            },
          ],
        },
        path: "edit-promo-code/:id",
        component: () => import("@/views/SystemManagement/PromoCodes/From"),
      },
      {
        name: "long-terms",
        meta: {
          title: "long-terms-agreements",
          requiresAuth: true,
          requiresPermission: "longTermAgreementTab",
          breadcrumb: [
            {
              text: "long-terms-agreements",
              disabled: false,
              active: false,
              to: "/long-terms",
            },
          ],
        },
        path: "long-terms",
        component: () => import("@/views/LongTerms/index.vue"),
      },
      {
        name: "lta-details",
        meta: {
          title: "details",
          requiresAuth: true,
          requiresPermission: "viewLongTermAgreement",
          breadcrumb: [
            {
              text: "long-terms-agreements",
              disabled: false,
              active: false,
              to: "/long-terms",
            },
            {
              text: "LTA#",
              disabled: false,
              active: false,
              to: "/lta-details/:id",
            },
          ],
        },
        path: "lta-details/:id",
        component: () => import("@/views/LongTerms/Details"),
      },
      {
        name: "add-long-term",
        meta: {
          title: "add-long-terms-agreement",
          requiresAuth: true,
          requiresPermission: "addLongTermAgreement",
          breadcrumb: [
            {
              text: "long-terms-agreements",
              disabled: false,
              active: false,
              to: "/long-terms",
            },
            {
              text: "add-long-terms-agreement",
              disabled: true,
              active: true,
              paramToRemove: "add-long-term",
            },
          ],
        },
        path: "add-long-term",
        component: () => import("@/views/LongTerms/Form/index.vue"),
      },
      {
        name: "edit-long-term",
        meta: {
          title: "edit-long-terms-agreement",
          requiresAuth: true,
          requiresPermission: "editLongTermAgreement",
          breadcrumb: [
            {
              text: "long-terms-agreements",
              disabled: false,
              active: false,
              to: "/long-terms",
            },
            {
              text: "edit-long-terms-agreement",
              disabled: true,
              active: true,
              paramToRemove: "edit-long-term/:id",
            },
          ],
        },
        path: "edit-long-term/:id",
        component: () => import("@/views/LongTerms/Form/index.vue"),
      },
      {
        name: "replace-requests-list",
        meta: {
          title: "replace-requests",
          requiresAuth: true,
          requiresPermission: "replaceRequestTab",
          breadcrumb: [
            {
              text: "replace-requests",
              disabled: false,
              active: false,
              to: "/replace-requests-list",
            },
          ],
        },
        path: "replace-requests-list",
        component: () => import("@/views/ReplaceRequest/index.vue"),
      },
      {
        name: "replace-request",
        meta: {
          title: "replace-request",
          requiresAuth: true,
          requiresPermission: "replaceRequestTab",
          breadcrumb: [
            {
              text: "replace-requests",
              disabled: false,
              active: false,
              to: "/replace-requests-list",
            },
            {
              text: "replace-request",
              disabled: true,
              active: true,
              paramToRemove: "replace-request",
            },
          ],
        },
        path: "replace-request",
        component: () => import("@/views/ReplaceRequest/Form/index.vue"),
      },
      {
        name: "tax-types",
        meta: {
          title: "tax-types",
          requiresAuth: true,
          requiresPermission: "taxTypeTab",
          breadcrumb: [
            {
              text: "tax-types",
              disabled: false,
              active: false,
              to: "/tax-types",
            },
          ],
        },
        path: "tax-types",
        component: () => import("@/views/TaxTypes/index.vue"),
      },
      {
        name: "add-tax-type",
        meta: {
          title: "add-tax-type",
          requiresAuth: true,
          requiresPermission: "addTaxType",
          breadcrumb: [
            {
              text: "tax-types",
              disabled: false,
              active: false,
              to: "/tax-types",
            },
            {
              text: "add-tax-type",
              disabled: true,
              active: true,
              paramToRemove: "add-tax-type",
            },
          ],
        },
        path: "add-tax-type",
        component: () => import("@/views/TaxTypes/Form/index.vue"),
      },
      {
        name: "edit-tax-type",
        meta: {
          title: "edit-tax-type",
          requiresAuth: true,
          requiresPermission: "editTaxType",
          breadcrumb: [
            {
              text: "tax-types",
              disabled: false,
              active: false,
              to: "/tax-types",
            },
            {
              text: "edit-tax-type",
              disabled: true,
              active: true,
              paramToRemove: "edit-tax-type/:id",
            },
          ],
        },
        path: "edit-tax-type/:id",
        component: () => import("@/views/TaxTypes/Form/index.vue"),
      },
      {
        name: "post-transactions",
        meta: {
          title: "post-transactions",
          requiresAuth: true,
          requiresPermission: "viewTransaction",
          breadcrumb: [
            {
              text: "post-transactions",
              disabled: false,
              active: false,
              to: "/post-transactions",
            },
          ],
        },
        path: "post-transactions",
        component: () => import("@/views/Finances/PostTransactions/index.vue"),
      },
      {
        name: "payment-voucher",
        meta: {
          title: "payment-voucher",
          requiresAuth: true,
          requiresPermission: "viewPaymentVoucher",
          breadcrumb: [
            {
              text: "payment-voucher",
              disabled: false,
              active: false,
              to: "/payment-voucher",
            },
          ],
        },
        path: "payment-voucher",
        component: () => import("@/views/Finances/PaymentVoucher/index.vue"),
      },
      {
        name: "add-payment-voucher",
        meta: {
          title: "add-payment-voucher",
          requiresAuth: true,
          requiresPermission: "addPaymentVoucher",
          breadcrumb: [
            {
              text: "payment-voucher",
              disabled: false,
              active: false,
              to: "/payment-voucher",
            },
            {
              text: "add-payment-voucher",
              disabled: true,
              active: true,
              paramToRemove: "add-payment-voucher",
            },
          ],
        },
        path: "add-payment-voucher",
        component: () =>
          import("@/views/Finances/PaymentVoucher/Form/index.vue"),
      },
      {
        name: "payment-voucher-view",
        meta: {
          title: "payment-voucher-view",
          requiresAuth: true,
          requiresPermission: "viewPaymentVoucher",
          breadcrumb: [
            {
              text: "post-transactions",
              disabled: false,
              active: false,
              to: "/post-transactions",
            },
            {
              text: "payment-voucher-view",
              disabled: true,
              active: true,
              paramToRemove: "payment-voucher-view/:id",
            },
          ],
        },
        path: "payment-voucher-view/:id",
        component: () =>
          import("@/views/Finances/PaymentVoucher/View/index.vue"),
      },
      // Business requirement [Keep it for future use]
      // {
      //   name: "statement",
      //   meta: {
      //     title: "statement",
      //     requiresAuth: true,
      //     requiresPermission: "financeClientStatmentTab",
      //     breadcrumb: [
      //       {
      //         text: "statement",
      //         disabled: false,
      //         active: false,
      //         to: "/statement",
      //       },
      //     ],
      //   },
      //   path: "statement",
      // component: () => import("@/views/Finances/Statement/index.vue"),
      // },
      {
        name: "messaging-center",
        meta: {
          title: "messaging-center",
          requiresAuth: true,
          requiresPermission: "listSupportChat",
          breadcrumb: [
            {
              text: "messaging-center",
              disabled: false,
              active: false,
              to: "/messaging-center",
            },
          ],
        },
        path: "messaging-center",
        component: () => import("@/views/MessagingCenter/index.vue"),
      },
      {
        name: "departure-arrival",
        meta: {
          title: "departure-arrival",
          requiresAuth: true,
          // requiresPermission: "DepartureArrival",
          breadcrumb: [
            {
              text: "departure-arrival",
              disabled: false,
              active: false,
              to: "/departure-arrival",
            },
          ],
        },
        path: "departure-arrival",
        component: () => import("@/views/DepartureArrival/index.vue"),
      },
      {
        name: "my-requests",
        meta: {
          title: "my-requests",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "my-requests",
              disabled: false,
              active: false,
              to: "/my-requests",
            },
          ],
        },
        path: "my-requests",
        component: () => import("@/views/MyRequests/index.vue"),
      },
      {
        name: "add-temporary-resources-request",
        meta: {
          title: "add-temporary-resources-request",
          requiresAuth: true,
          requiresPermission: "addTemporaryResourcesRequest",
          breadcrumb: [
            {
              text: "my-requests",
              disabled: false,
              active: false,
              to: "/my-requests",
            },
            {
              text: "add-new-request",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "add-temporary-resources-request",
        component: () =>
          import("@/views/MyRequests/TemporaryResourcesRequest/Add.vue"),
      },
      {
        name: "extend-temporary-resources-request",
        meta: {
          title: "extend-temporary-resources-request",
          requiresAuth: true,
          requiresPermission: "addTemporaryResourcesRequest",
          breadcrumb: [
            {
              text: "my-requests",
              disabled: false,
              active: false,
              to: "/my-requests",
            },
            {
              text: "extend-new-request",
              disabled: true,
              active: true,
            },
          ],
        },
        path: "extend-temporary-resources-request/:id",
        component: () =>
          import("@/views/MyRequests/TemporaryResourcesRequest/Add.vue"),
      },
      {
        name: "add-missing-resources-request",
        meta: {
          title: "add-missing-resources-request",
          requiresAuth: true,
          requiresPermission: "addTemporaryResourcesRequest",
          breadcrumb: [
            {
              text: "add-missing-resources-request",
              disabled: false,
              active: false,
              to: { name: "add-missing-resources-request" },
            },
          ],
        },
        path: "add-missing-resources-request",
        component: () => import("@/views/MissingRequests/Add/index.vue"),
      },
      {
        name: "resource-requests",
        meta: {
          title: "resource-requests",
          requiresAuth: true,
          requiresPermission: "viewTemporaryResourcesRequest",
          breadcrumb: [
            {
              text: "resource-requests",
              disabled: false,
              active: false,
              to: "/resource-requests",
            },
          ],
        },
        path: "resource-requests",
        component: () => import("@/views/ResourceRequests/index.vue"),
      },
      {
        name: "replace-requests",
        meta: {
          title: "replace-requests",
          requiresAuth: true,
          requiresPermission: "timeOffRequest",
          breadcrumb: [
            {
              text: "replace-requests",
              disabled: false,
              active: false,
              to: "/replace-requests",
            },
          ],
        },
        path: "replace-requests",
        component: () => import("@/views/ReplaceRequests/index.vue"),
      },
      {
        name: "time-off-requests",
        meta: {
          title: "time-off-requests",
          requiresAuth: true,
          requiresPermission: "timeOffRequest",
          breadcrumb: [
            {
              text: "time-off-requests",
              disabled: false,
              active: false,
              to: "/time-off-requests",
            },
          ],
        },
        path: "time-off-requests",
        component: () => import("@/views/TimeOffRequest/index.vue"),
      },
      {
        name: "reservations-requests",
        meta: {
          title: "reservations-requests",
          requiresAuth: true,
          requiresPermission: "viewReservationRequest",
          breadcrumb: [
            {
              text: "reservations-requests",
              disabled: false,
              active: false,
              to: "/reservations-requests",
            },
          ],
        },
        path: "reservations-requests",
        component: () => import("@/views/ReservationsRequests/index.vue"),
      },
      {
        name: "allowance-requests",
        meta: {
          title: "allowance-requests",
          requiresAuth: true,
          requiresPermission: "viewDriverAllowance",
          breadcrumb: [
            {
              text: "allowance-requests",
              disabled: false,
              active: false,
              to: "/allowance-requests",
            },
          ],
        },
        path: "allowance-requests",
        component: () => import("@/views/AllowanceRequests/index.vue"),
      },
      {
        name: "request-details",
        meta: {
          title: "request",
          requiresPermission: ["manageAllTasks", "viewMyTasks"],
          requiresAuth: true,
          breadcrumb: [
            {
              text: "requests",
              disabled: false,
              active: false,
              to: "/workflow-requests",
            },
            {
              text: "request-details",
              disabled: false,
              active: false,
              to: "/workflow-request/:id",
            },
          ],
        },
        path: "workflow-request/:id",
        component: () => import("@/views/WorkflowRequests/WorkflowDetails.vue"),
      },
      {
        name: "deadhead-trip",
        meta: {
          title: "deadhead-trip",
          requiresAuth: true,
          // requiresPermission: "",
          breadcrumb: [
            {
              text: "deadhead-trip",
              disabled: false,
              active: false,
              to: { name: "deadhead-trip" },
            },
          ],
        },
        path: "deadhead-trip",
        component: () => import("@/views/DeadheadTrip/index.vue"),
      },
      {
        name: "change-password",
        meta: {
          title: "change-password",
          requiresAuth: true,
          breadcrumb: [
            {
              text: "change-password",
              disabled: false,
              active: false,
              to: { name: "change-password" },
            },
          ],
        },
        path: "change-password",
        component: () => import("@/views/ChangePassword/index.vue"),
      },
      {
        name: "contracts",
        meta: {
          title: "contract-listing",
          requiresAuth: true,
          requiresPermission: "contractTab",
          breadcrumb: [
            {
              text: "contract-listing",
              disabled: false,
              active: false,
              to: { name: "contract-listing" },
            },
          ],
        },
        path: "contracts",
        component: () => import("@/views/Contracts/Listing/index.vue"),
      },
      {
        name: "add-route",
        meta: {
          title: "add-route",
          requiresAuth: true,
          requiresPermission: "createContractRoute",
          breadcrumb: [
            {
              text: "contracts",
              disabled: false,
              active: false,
              to: "/contracts",
            },
            {
              text: "add-route",
              disabled: false,
              active: false,
              to: "contract/:id/routes/add",
            },
          ],
        },
        path: "contract/:id/routes/add",
        component: () => import("@/views/Routes/Add/index.vue"),
      },
      {
        name: "workflow",
        meta: {
          title: "workflow-listing",
          requiresAuth: true,
          requiresPermission: "viewWorkflow",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "workflow-listing",
              disabled: false,
              active: false,
              to: { name: "workflow-listing" },
            },
          ],
        },
        path: "workflow-listing",
        component: () =>
          import("@/views/SystemManagement/Workflow/Listing/index.vue"),
      },
      {
        name: "add-workflow",
        meta: {
          title: "add-workflow",
          requiresAuth: true,
          requiresPermission: "addWorkflow",
          breadcrumb: [
            {
              text: "system-management",
              disabled: false,
              active: false,
              to: "/system-management",
            },
            {
              text: "workflow-listing",
              disabled: false,
              active: false,
              to: "/workflow-listing",
            },
            {
              text: "create-workflow",
              disabled: true,
              active: true,
              to: "workflow/add",
            },
          ],
        },
        path: "workflow/add",
        component: () =>
          import("@/views/SystemManagement/Workflow/Add/index.vue"),
      },
      {
        name: "financial-claim",
        meta: {
          title: "financial-claim",
          requiresAuth: true,
          requiresPermission: "viewFinancialClaim",
          breadcrumb: [
            {
              text: "financial-claim",
              disabled: false,
              active: false,
              to: "/financial-claim",
            },
          ],
        },
        path: "financial-claim",
        component: () =>
          import("@/views/Finances/FinancialClaim/Listing/index.vue"),
      },
      {
        name: "create-financial-claim",
        meta: {
          title: "financial-claim",
          requiresAuth: true,
          requiresPermission: "addFinancialClaim",
          breadcrumb: [
            {
              text: "financial-claim",
              disabled: false,
              active: false,
              to: "/financial-claim",
            },
            {
              text: "create-financial-claim",
              disabled: true,
              active: false,
              to: "/financial-claim/add",
            },
          ],
        },
        path: "financial-claim/add",
        component: () =>
          import("@/views/Finances/FinancialClaim/Create/index.vue"),
      },
      {
        name: "workflow-requests",
        meta: {
          title: "workflow-requests",
          requiresAuth: true,
          requiresPermission: ["manageAllTasks", "viewMyTasks"],
          breadcrumb: [
            {
              text: "workflow-requests",
              disabled: false,
              active: false,
              to: "/workflow-requests",
            },
          ],
        },
        path: "workflow-requests",
        component: () => import("@/views/WorkflowRequests/index.vue"),
      },
    ],
  },
  {
    name: "permission-denied",
    meta: { title: "permission-denied", requiresAuth: true },
    path: "/permission-denied",
    component: PermissionDenied,
  },
  {
    name: "not-found",
    meta: { title: "not-found", requiresAuth: true },
    path: "/:notFound(.*)",
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    window.scrollTo(0, 0);
  },
});

router.beforeResolve((to, from, next) => {
  const lang = to.query.lang || "en";
  // Set the language in the i18n object
  i18n.locale = lang;
  next();
});

const checkPermission = async (to) => {
  if (to.matched.some((record) => record.meta.requiresPermission)) {
    const permissionName = to.meta.requiresPermission;
    if (!store.getters["enums/user_info_loaded"])
      await store.dispatch("enums/GET_USER_INFO");
    // return !store.getters["enums/is_not_permitted"](permissionName);
    if (permissionName) {
      // If it's an array, check if user has ANY of the permissions
      if (Array.isArray(permissionName)) {
        return permissionName.some(
          (permission) => !store.getters["enums/is_not_permitted"](permission),
        );
      }
      // Single permission check
      return !store.getters["enums/is_not_permitted"](permissionName);
    }
  } else {
    return true;
  }
};

router.beforeEach(async (to, _, next) => {
  await store.dispatch("enums/GET_OPERATOR", "eJ");
  window.document.title = to.meta.title
    ? `${i18n.t(store.getters["enums/operator"])} - ${i18n.t(to.meta.title)}`
    : store.getters["enums/operator"];
  const havePermission = await checkPermission(to);
  if (havePermission) {
    if (
      to.meta.openURL ||
      (to.meta.requiresAuth && store.getters["auth/authenticated"]) ||
      (!to.meta.requiresAuth && !store.getters["auth/authenticated"])
    ) {
      next();
    } else if (to.meta.requiresAuth && !store.getters["auth/authenticated"]) {
      localStorage.clear();
      next({ name: "login" });
    } else if (!to.meta.requiresAuth && store.getters["auth/authenticated"]) {
      next({ name: "dashboard" });
    } else if (to.path.length > 1) {
      next();
    } else if (store.getters["auth/authenticated"]) {
      next({ name: "dashboard" });
    } else {
      localStorage.clear();
      next({ name: "login" });
    }
  } else {
    next({ name: "permission-denied" });
  }
});

export default router;
